/* eslint-disable-next-line no-restricted-imports */
import { useFeatureGate, useStatsigClient } from '@statsig/react-bindings';

import { FeatureFlagId } from '@craft/feature-flags';
import { type NonEmptyArray } from '@craft/lib';
import { titleCase, type TitleCase } from '@craft/lib/formatters';

interface HasFeatureFlagParams {
  readonly isInverted?: boolean;
}

type AnyOrAll =
  | { readonly all: NonEmptyArray<FeatureFlagId>; readonly any?: never }
  | { readonly all?: never; readonly any: NonEmptyArray<FeatureFlagId> };

export type FeatureFlagDesignation = AnyOrAll | FeatureFlagId | FeatureFlagId[];

const evaluateDesignation = (
  designation: Exclude<FeatureFlagDesignation, FeatureFlagId>,
  evaluator: (name: FeatureFlagId) => boolean,
): boolean => {
  if (Array.isArray(designation)) {
    return designation.every(evaluator);
  } else if (designation.all) {
    return designation.all.every(evaluator);
  }
  return designation.any.some(evaluator);
};

type UseHasFeatureFlagReturn<F extends FeatureFlagDesignation | undefined> = F extends undefined
  ? undefined
  : boolean;

const useLocalHasFeatureFlag = <F extends FeatureFlagDesignation | undefined>(
  flag: F,
  params?: HasFeatureFlagParams,
): UseHasFeatureFlagReturn<F> => {
  const { checkGate } = useStatsigClient();
  const { value: flagCheck } = useFeatureGate(typeof flag === 'string' ? flag : '');

  const value =
    typeof flag === 'string' || flag === undefined
      ? flagCheck
      : evaluateDesignation(flag, checkGate);

  if (flag === undefined) {
    return undefined as UseHasFeatureFlagReturn<F>;
  }
  if (params?.isInverted === true) {
    return !value as UseHasFeatureFlagReturn<F>;
  }
  return value as UseHasFeatureFlagReturn<F>;
};

export type UseHasFeatureFlagPartials = {
  [key in FeatureFlagId as TitleCase<key, true>]: (params?: HasFeatureFlagParams) => boolean;
};

/**
 * A hook that indicates whether or not the user has the specified feature flag,
 * {@link FeatureFlagId}.
 *
 * The hook can either be used directly or via any one of its partials, each of which is associated
 * with a specific feature flag, {@link FeatureFlagId}.
 *
 * @see {@link FeatureFlagId}
 *
 * @example
 * // Using the component directly.
 * const hasFeatureFlag = useHasFeatureFlag(FeatureFlagId.MmfCredentialing);
 * @example
 * // Using the component via a partial.
 * const hasFeatureFlag = useHasFeatureFlag.MmfCredentialing();
 */
export const useHasFeatureFlag = Object.assign(useLocalHasFeatureFlag, {
  ...Object.values(FeatureFlagId).reduce(
    (prev, flag) => ({
      ...prev,
      [titleCase(flag, true)]: (params?: HasFeatureFlagParams) =>
        useLocalHasFeatureFlag(flag, params),
    }),
    {} as UseHasFeatureFlagPartials,
  ),
});
